/**
 * Per-feature upgrade copy for the FeatureUpgradeModal.
 *
 * Intentionally terse - videos (5-10s looped clips) will carry most of the
 * explanation. Only gated features have entries; ungated features return null.
 *
 * @module lib/featureCatalog
 */

import type { FeatureKey } from './entitlements';

/** Copy and assets for a single upgrade-gated feature. */
export interface FeatureCopy {
  /** Short, feature-focused title (<=40 chars). */
  title: string;
  /** One-line benefit statement (<=80 chars). */
  tagline: string;
  /** Up to 3 scan-ready value points (<=60 chars each). */
  bullets: string[];
  /** Plan name required to unlock this feature. */
  requiredPlan: 'Pro' | 'Business' | 'Enterprise';
  /** Looped gif-style video showcasing the feature in use. Undefined until assets ship. */
  videoUrl?: string;
}

/** Subset of FeatureKey that is actually gated (has an entry). */
export type GatedFeatureKey = Exclude<FeatureKey, 'basic_analytics' | 'multi_language_interviews' | 'embed_widget' | 'extended_key_topics'>;

/** Hardcoded copy for each gated feature. */
export const FEATURE_CATALOG: Record<GatedFeatureKey, FeatureCopy> = {
  auto_recipes: {
    title: 'Automatic recipes',
    tagline: 'Run a recipe whenever a conversation is submitted.',
    bullets: ['One automatic recipe per campaign', 'Results on your overview', 'Choose when to switch it on'],
    requiredPlan: 'Pro',
  },
  audio_interviews: {
    title: 'Audio calls',
    tagline: 'Hear the story in their own voice.',
    bullets: ['Live conversations with hollie', 'Recorded and transcribed', 'Pause and resume the same included call'],
    requiredPlan: 'Pro',
  },
  video_interviews: {
    title: 'Video calls',
    tagline: 'Capture body language and tone, not just words.',
    bullets: [
      'Camera + audio recording',
      'Richer testimonial clips',
      'Better engagement signal',
    ],
    requiredPlan: 'Pro',
  },
  screen_sharing: {
    title: 'Screen sharing',
    tagline: 'Let respondents show, not just tell.',
    bullets: [
      'hollie can ask for a live screen share',
      'Works on audio and video calls',
      'Captured alongside the session recording',
    ],
    requiredPlan: 'Business',
  },
  advanced_analytics: {
    title: 'Advanced analytics',
    tagline: 'Go beyond basic counts: see sentiment and themes.',
    bullets: [
      'Sentiment journey per call',
      'Aggregated theme breakdown',
      'Deeper response insights',
    ],
    requiredPlan: 'Business',
  },
  custom_branding: {
    title: 'Custom branding',
    tagline: 'Make every conversation feel like yours.',
    bullets: [
      'Your logo and colours',
      'Custom welcome message',
      'Branded share pages',
    ],
    requiredPlan: 'Pro',
  },
  remove_holito_referral: {
    title: 'Remove holito branding',
    tagline: 'Give your brand the whole hosted experience.',
    bullets: [
      'No Try holito attribution',
      'Cleaner share experience',
      'Own the full brand moment',
    ],
    requiredPlan: 'Business',
  },
  completion_redirect: {
    title: 'Redirect on completion',
    tagline: 'Send respondents straight to a thank-you page after the conversation.',
    bullets: [
      'Auto-redirect when the conversation ends',
      'Works on direct links and embeds',
      'Continue the journey on your site',
    ],
    requiredPlan: 'Pro',
  },
  api_access: {
    title: 'API access',
    tagline: 'Let agents and scripts work with your conversations.',
    bullets: [
      'Connect Claude, Cursor, or your own agent',
      'Pull conversations and share links by API',
      'Create and manage campaigns programmatically',
    ],
    requiredPlan: 'Pro',
  },
};

/**
 * Returns the upgrade copy for a feature, or null when the feature is
 * available on all plans (ungated).
 */
export function getFeatureCopy(feature: FeatureKey): FeatureCopy | null {
  return (FEATURE_CATALOG as Record<string, FeatureCopy>)[feature] ?? null;
}
